import { mergeTemplateCreationRecordUseSections } from '@/features/templateBuilder/utils/templateCreationRecordUseSections';
import { getTemplateCreationRecordById } from '@/pages/api/scribeAPIV2';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { isTemplateDetailsModalOpenAtom } from '@heidi/templates';
import type { GetTemplateCreationRecordResponse } from '@heidi/templates/contracts';
import { GET_TEMPLATE_CREATION_RECORD_BY_ID } from '@heidi/templates/contracts';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';

export { GET_TEMPLATE_CREATION_RECORD_BY_ID };
// The invalidator moved behind the data door; the hook below stays app-side on
// its router, jotai and app-queryClient couplings.
export { invalidateTemplateCreationRecordById } from '@heidi/templates/data';

type SelectTemplateCreationRecordResponse<T> = (
  data: GetTemplateCreationRecordResponse
) => T;

const mergeFetchedTemplateCreationRecord = (
  templateCreationRecordId: string,
  incoming: GetTemplateCreationRecordResponse
) =>
  mergeTemplateCreationRecordUseSections(
    incoming,
    reactQueryClient.getQueryData<GetTemplateCreationRecordResponse>([
      GET_TEMPLATE_CREATION_RECORD_BY_ID,
      { templateCreationRecordId },
    ])
  );

export const fetchTemplateCreationRecordById = async (
  templateCreationRecordId: string
) => {
  return await reactQueryClient.fetchQuery({
    queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
    queryFn: async () => {
      const { data } = await getTemplateCreationRecordById(templateCreationRecordId);
      return mergeFetchedTemplateCreationRecord(templateCreationRecordId, data);
    },
    staleTime: 0,
  });
};

export function useQueryTemplateCreationRecordById<
  T = GetTemplateCreationRecordResponse,
>({
  templateCreationRecordId,
  select,
}: {
  templateCreationRecordId: string | undefined;
  select: SelectTemplateCreationRecordResponse<T>;
}) {
  const [isTemplateDetailsModalOpen, setIsTemplateDetailsModalOpen] = useAtom(
    isTemplateDetailsModalOpenAtom
  );
  const router = useRouter();

  return useQuery(
    {
      queryKey: [
        GET_TEMPLATE_CREATION_RECORD_BY_ID,
        { templateCreationRecordId: templateCreationRecordId ?? '' },
      ],
      select,
      queryFn: async () => {
        try {
          const { data } = await getTemplateCreationRecordById(
            templateCreationRecordId ?? ''
          );

          return mergeFetchedTemplateCreationRecord(templateCreationRecordId ?? '', data);
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 403) {
            if (isTemplateDetailsModalOpen) {
              setIsTemplateDetailsModalOpen(false);
            } else {
              void router.push('/my-templates');
            }
          }
          throw error;
        }
      },
      enabled: !!templateCreationRecordId,
    },
    reactQueryClient
  );
}
